import moment from "moment";
import {
  formatSubscriptionStatusLabel,
  getSubscriptionStatusVariant,
} from "./tenantOverview.utils";

export const formatParticipantLimitUsage = (participantLimit = {}) => {
  const { plan_limit: planLimit, active_count: activeCount } = participantLimit;

  if (planLimit === null || planLimit === undefined) {
    if (activeCount === null || activeCount === undefined) {
      return "—";
    }

    return `${activeCount} active`;
  }

  return `${activeCount ?? 0} / ${planLimit}`;
};

export const getParticipantLimitVariant = (participantLimit = {}) => {
  if (participantLimit.read_only_due_to_participant_limit) {
    return "danger";
  }

  if (participantLimit.over_limit) {
    return "warning";
  }

  if (participantLimit.plan_limit !== null && participantLimit.plan_limit !== undefined) {
    return "success";
  }

  return "neutral";
};

export const formatBillingTimestamp = (value) => {
  if (!value) {
    return "—";
  }

  const timestamp = moment(value);
  if (!timestamp.isValid()) {
    return "—";
  }

  return timestamp.format("D MMM YYYY, h:mm a");
};

export const buildBillingAlerts = (tenant) => {
  if (!tenant) {
    return [];
  }

  const alerts = [];
  const subscription = tenant.subscription || {};
  const billing = tenant.billing || {};
  const participantLimit = billing.participant_limit || {};
  const flags = tenant.flags || {};

  if (["unpaid", "past_due", "incomplete_expired"].includes(subscription.current_status)) {
    alerts.push({
      id: "payment-failed",
      title: "Subscription payment issue",
      message: formatSubscriptionStatusLabel(subscription),
      variant: "danger",
    });
  }

  if (subscription.payment_error) {
    alerts.push({
      id: "payment-error",
      title: "Stripe payment error",
      message: subscription.payment_error,
      variant: "danger",
    });
  }

  if (participantLimit.read_only_due_to_participant_limit) {
    alerts.push({
      id: "participant-limit-read-only",
      title: "Read-only due to participant limit",
      message: "Tenant exceeded plan participant limit and grace period has ended.",
      variant: "danger",
    });
  } else if (participantLimit.over_limit) {
    const daysLeft =
      participantLimit.days_until_read_only === null ||
      participantLimit.days_until_read_only === undefined
        ? null
        : participantLimit.days_until_read_only;

    alerts.push({
      id: "participant-limit-warning",
      title: "Participant limit exceeded",
      message:
        daysLeft === null
          ? `${formatParticipantLimitUsage(participantLimit)} active participants.`
          : `${formatParticipantLimitUsage(participantLimit)} — read-only in ${daysLeft} day${
              daysLeft === 1 ? "" : "s"
            } unless upgraded.`,
      variant: "warning",
    });
  }

  if (billing.manual_read_only && !flags.participant_limit_read_only) {
    alerts.push({
      id: "manual-read-only",
      title: "Manual read-only enabled",
      message: "Platform staff forced read-only mode for this tenant.",
      variant: "warning",
    });
  }

  if (flags.wildcard) {
    alerts.push({
      id: "wildcard",
      title: "Wildcard billing",
      message: "Commercial wildcard concessions apply — participant limits are not enforced.",
      variant: "neutral",
    });
  }

  return alerts;
};

export const getBillingStatusBadge = (tenant) => {
  const subscription = tenant?.subscription || {};
  const flags = tenant?.flags || {};

  return {
    label: formatSubscriptionStatusLabel(subscription),
    variant: getSubscriptionStatusVariant(subscription, flags),
  };
};

export const formatBillingStateLabel = (billingState) => {
  switch (billingState) {
    case "active":
      return "Active subscription";
    case "trial":
      return "On trial";
    case "payment_failed":
      return "Payment failed";
    case "wildcard":
      return "Wildcard billing";
    case "inactive":
      return "No active subscription";
    case "unconfigured":
      return "Not configured";
    default:
      return billingState || "Unknown";
  }
};

export const getBillingStateVariant = (billingState) => {
  switch (billingState) {
    case "active":
      return "success";
    case "trial":
      return "warning";
    case "payment_failed":
      return "danger";
    case "wildcard":
      return "neutral";
    case "inactive":
    case "unconfigured":
      return "neutral";
    default:
      return "neutral";
  }
};

export const buildTenantFinancialMetrics = (tenant) => {
  if (!tenant) {
    return [];
  }

  const subscription = tenant.subscription || {};
  const billing = tenant.billing || {};
  const financial = tenant.financial || {};
  const participantLimit = billing.participant_limit || {};
  const billingStatus = getBillingStatusBadge(tenant);

  return [
    {
      id: "billing-state",
      label: "Financial state",
      value: formatBillingStateLabel(financial.billing_state),
      variant: getBillingStateVariant(financial.billing_state),
    },
    {
      id: "plan",
      label: "Plan",
      value: subscription.plan_name || "—",
    },
    {
      id: "stripe-status",
      label: "Stripe status",
      value: billingStatus.label,
      variant: billingStatus.variant,
    },
    {
      id: "plan-usage",
      label: "Participant usage",
      value:
        participantLimit.plan_limit !== null && participantLimit.plan_limit !== undefined
          ? formatParticipantLimitUsage(participantLimit)
          : "—",
      variant: getParticipantLimitVariant(participantLimit),
    },
    {
      id: "read-only",
      label: "Write access",
      value: tenant.flags?.read_only ? "Read-only" : "Writable",
      variant: tenant.flags?.read_only ? "danger" : "success",
    },
    {
      id: "wildcard",
      label: "Wildcard",
      value: tenant.flags?.wildcard ? "Enabled" : "Standard",
      variant: tenant.flags?.wildcard ? "warning" : "neutral",
    },
  ];
};

export const buildTenantBillingSummaryChips = (tenant, paymentHistory = null) => {
  if (!tenant) {
    return [];
  }

  const subscription = tenant.subscription || {};
  const financial = tenant.financial || {};
  const billing = tenant.billing || {};
  const summary = paymentHistory?.summary || {};
  const currency = paymentHistory?.currency || summary.currency || "AUD";
  const billingStatus = getBillingStatusBadge(tenant);

  const chips = [
    {
      id: "billing-state",
      label: "Financial state",
      value: formatBillingStateLabel(financial.billing_state),
      variant:
        financial.billing_state === "payment_failed"
          ? "danger"
          : financial.billing_state === "trial"
            ? "attention"
            : financial.billing_state === "active"
              ? "success"
              : "default",
    },
    {
      id: "plan",
      label: "Plan",
      value: subscription.plan_name || "—",
    },
    {
      id: "stripe-status",
      label: "Stripe status",
      value: billingStatus.label,
      variant:
        billingStatus.variant === "danger"
          ? "danger"
          : billingStatus.variant === "warning"
            ? "attention"
            : "default",
    },
  ];

  if (subscription.on_trial && subscription.trial_end_date) {
    chips.push({
      id: "trial-ends",
      label: "Trial ends",
      value: formatBillingTimestamp(subscription.trial_end_date).split(",")[0],
      variant: "attention",
    });
  }

  if (summary.upcoming_at) {
    chips.push({
      id: "next-renewal",
      label: "Next renewal",
      value: formatBillingTimestamp(summary.upcoming_at).split(",")[0],
    });
  } else if (subscription.next_payment_attempt || billing.next_payment_attempt) {
    chips.push({
      id: "next-attempt",
      label: "Next payment attempt",
      value: formatBillingTimestamp(
        subscription.next_payment_attempt || billing.next_payment_attempt
      ).split(",")[0],
      variant: "attention",
    });
  }

  if (paymentHistory?.has_stripe_customer) {
    const outstandingCents = summary.outstanding_cents ?? 0;
    chips.push({
      id: "outstanding",
      label: "Outstanding",
      value:
        outstandingCents > 0
          ? new Intl.NumberFormat("en-AU", {
              style: "currency",
              currency,
            }).format(outstandingCents / 100)
          : "$0.00",
      variant: outstandingCents > 0 ? "danger" : "success",
    });

    chips.push({
      id: "collected-30d",
      label: "Collected (30d)",
      value: new Intl.NumberFormat("en-AU", {
        style: "currency",
        currency,
      }).format((summary.collected_last_30_days_cents || 0) / 100),
    });
  }

  return chips;
};
